import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Copy } from "lucide-react";
import { BrandWord } from "@/components/BrandWord";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState(null); // dev helper

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // Dev mode: backend returns the reset_link until email integration is added
      if (res.data?.reset_link) setResetLink(res.data.reset_link);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-8 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo192.png" alt="ShiftQuote" className="w-8 h-8 object-contain shrink-0" />
          <BrandWord className="font-heading text-lg font-black tracking-tight" />
        </Link>
        <Link to="/login" className="btn-ghost flex items-center gap-2" data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-white/10 bg-[#121214] p-8 sm:p-10">
          <div className="label-eyebrow mb-4">PASSWORD RESET</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">Forgot password?</h1>
          <p className="text-sm text-zinc-400 mb-8">Enter your email and we'll send a reset link.</p>

          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-2">EMAIL</label>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base"
                  placeholder="you@garage.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit-btn">
                {loading ? "..." : "Send reset link"}
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="border border-white/10 bg-[#09090B] p-4 text-sm text-zinc-300">
                If <span className="font-medium text-white">{email}</span> is registered, a reset link has been issued. Check your inbox.
              </div>

              {resetLink && (
                <div className="border border-[#FF9500]/40 bg-[#FF9500]/5 p-4">
                  <div className="label-eyebrow text-[#FF9500] mb-2">DEV MODE — RESET LINK</div>
                  <p className="text-xs text-zinc-400 mb-3">Email integration isn't connected yet. Use this link to reset:</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={resetLink} className="input-base flex-1 text-xs font-mono" data-testid="forgot-dev-link" />
                    <button onClick={copy} className="btn-secondary flex items-center gap-1.5 px-3" data-testid="forgot-copy-btn">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              <Link to="/login" className="btn-secondary w-full inline-block text-center">Return to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
